import React from "react";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import {
  QuestionConfigAnswerOptionDto,
  QuestionConfigDetailDto,
} from "@green0meter/api-client";

import { Button, IconButton, Stack } from "@components/base";
import {
  emptyAnswerOptionTranslations,
  useQuestionEditorStore,
} from "@components/main/questions/QuestionEditor";
import { generateId } from "@helpers";

interface SelectInputProps {
  questionConfig: QuestionConfigDetailDto;
  setQuestionConfig: (questionConfig: QuestionConfigDetailDto) => void;
}

const SelectInput: React.FC<SelectInputProps> = ({
  questionConfig,
  setQuestionConfig,
}) => {
  const { language } = useQuestionEditorStore();
  const { t } = useTranslation();

  const addAnswerOption = (): void => {
    const newAnswer: QuestionConfigAnswerOptionDto = {
      id: generateId(),
      translations: emptyAnswerOptionTranslations,
    };

    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions: questionConfig.answerOptions
          ? [...questionConfig.answerOptions, newAnswer]
          : [newAnswer],
      });
    }
  };

  const handleOptionChange = (index: number, value: string): void => {
    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions:
          questionConfig.answerOptions?.map((answerOption, i) =>
            index === i
              ? {
                  ...answerOption,
                  translations: answerOption.translations.map((translation) =>
                    translation.language === language
                      ? { ...translation, name: value }
                      : translation,
                  ),
                }
              : answerOption,
          ) || [],
      });
    }
  };

  const handleOptionRemove = (index: number): void => {
    if (questionConfig) {
      setQuestionConfig({
        ...questionConfig,
        answerOptions: questionConfig.answerOptions?.filter((_, i) => index !== i),
      });
    }
  };

  return questionConfig ? (
    <Stack spacing={1}>
      {questionConfig.answerOptions?.map((answerOption, index) => {
        const optionTranslation = answerOption.translations.find(
          (translation) => translation.language === language,
        );

        return (
          <Stack direction="row">
            <TextField
              value={optionTranslation?.name || ""}
              placeholder={t("questionEditor.enterAnswer")}
              variant="standard"
              fullWidth
              onChange={(e) => handleOptionChange(index, e.target.value)}
            />
            <IconButton size="small" onClick={() => handleOptionRemove(index)}>
              <FeatherIcon icon="minus-circle" />
            </IconButton>
          </Stack>
        );
      })}
      <Stack direction="row">
        <Button
          variant="text"
          size="small"
          onClick={addAnswerOption}
          startIcon={<FeatherIcon icon="plus" />}
        >
          {t("questionEditor.addAnswer")}
        </Button>
      </Stack>
    </Stack>
  ) : null;
};

export default SelectInput;
